import type { BadgeTone } from '@/components/ui';

/** Panel claim states (API ClaimStatus) as shown in Admin v2. */
export const CLAIM_STATUS: Record<string, { label: string; tone: BadgeTone }> =
  {
    draft: { label: 'Draft', tone: 'neutral' },
    blocked: { label: 'Blocked', tone: 'danger' },
    ready: { label: 'Ready', tone: 'info' },
    submitted: { label: 'Submitted', tone: 'warning' },
    queried: { label: 'Queried', tone: 'warning' },
    paid: { label: 'Paid', tone: 'success' },
    part_paid: { label: 'Part paid', tone: 'warning' },
    rejected: { label: 'Rejected', tone: 'danger' },
    written_off: { label: 'Written off', tone: 'neutral' },
    billed_patient: { label: 'Billed to patient', tone: 'neutral' },
  };

export const claimStatus = (s: string) =>
  CLAIM_STATUS[s] ?? { label: s, tone: 'neutral' as BadgeTone };

/** Prototype claim tabs: Open / Submitted / Closed. */
export const CLAIM_TABS = {
  // RL-1767: a rejected claim still needs Bill patient or Write off claim.
  open: ['draft', 'blocked', 'ready', 'queried', 'rejected'],
  submitted: ['submitted', 'part_paid'],
  closed: ['paid', 'written_off', 'billed_patient'],
} as const;

/** "20 h left" / "overdue 6 h" / "4 d 4 h" from hours left. */
export function slaLabel(hoursLeft: number | null) {
  if (hoursLeft === null) return null;
  if (hoursLeft < 0)
    return { text: `overdue ${Math.abs(hoursLeft)} h`, late: true };
  if (hoursLeft >= 48)
    return {
      text: `${Math.floor(hoursLeft / 24)} d ${hoursLeft % 24} h`,
      late: false,
    };
  return { text: `${hoursLeft} h left`, late: hoursLeft < 24 };
}

/** No countdown once a claim is closed (paid, written off, billed to patient; RL-1778, #147). */
export function claimSla(claim: { status: string; hoursLeft: number | null }) {
  if ((CLAIM_TABS.closed as readonly string[]).includes(claim.status))
    return null;
  return slaLabel(claim.hoursLeft);
}

/**
 * The claim number staff quote (CLM-…), never the bill number (RL-1778,
 * #146). Claims made before numbering get the API's fallback, the same one
 * Panel receivables and Patient panel usage show.
 */
export function claimNumber(claim: { id: string; reference?: string | null }) {
  return claim.reference ?? `CLM-${claim.id.slice(-8).toUpperCase()}`;
}

/**
 * What to do with a remittance deduction: the same three buttons on
 * Knock-off and in Panel receivables (RL-1778, #148). The choice is only
 * recorded until "Confirm knock-off" applies the remittance; then the
 * write-off or patient bill happens. The API stores the older
 * `accept_deduction` as a write-off.
 */
export const DEDUCTION_DECISIONS = [
  {
    value: 'dispute',
    label: 'Dispute',
    pending: 'Will dispute',
    done: 'Disputed',
  },
  {
    value: 'write_off',
    label: 'Write off',
    pending: 'Will write off',
    done: 'Written off',
  },
  {
    value: 'bill_patient',
    label: 'Bill patient',
    pending: 'Will bill patient',
    done: 'Billed to patient',
  },
] as const;

export type DeductionDecision = (typeof DEDUCTION_DECISIONS)[number]['value'];

/**
 * A saved decision in words, never `write_off`: "Will write off" until the
 * remittance is applied (knocked off), "Written off" after.
 */
export function deductionDecisionLabel(decision: string, applied: boolean) {
  const key = decision === 'accept_deduction' ? 'write_off' : decision;
  const found = DEDUCTION_DECISIONS.find((d) => d.value === key);
  if (!found) return decision.replaceAll('_', ' ');
  return applied ? found.done : found.pending;
}
